import { createFileRoute } from '@tanstack/react-router'
import { Trash2 } from 'lucide-react'

import { ProductStatus } from '@/api/admin-products-types'
import { useCreateProduct } from '@/api/api-mutations'
import { PricingMethod } from '@/api/common-types'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { toast, useCreatedToast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, FormLabel, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'
import { Plus } from '@/icons/plus'

export const Route = createFileRoute('/_authenticated/_general/products/create')({
	component: RouteComponent,
	staticData: {
		crumb: ({ context: { L } }) => L.Product.CreateTitle()
	}
})

function RouteComponent() {
	return (
		<RouteModal asDrawer>
			<NewProductForm />
		</RouteModal>
	)
}

function NewProductForm() {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()
	const createdToast = useCreatedToast()

	const createProduct = useCreateProduct()

	const form = useAppForm({
		defaultValues: {
			status: ProductStatus.Draft,
			title: '',
			subtitle: '',
			description: '',
			sku: '',
			pricingMethod: PricingMethod.PerUnit,
			barcodes: [] as string[]
		},
		onSubmit: async ({ value }) => {
			// TODO Add an alert notifying the user about price changes when the pricing method is changed to or from price embedded

			value.barcodes = value.barcodes.filter(b => b)
			const result = await createProduct({ body: value })
			if (result.isSuccess) {
				createdToast(L.Product.EditSuccessToast({ title: value.title }), {
					to: '/products/$productUuid',
					params: { productUuid: result.response.uuid }
				})
				handleSuccess()
			} else {
				toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
			}
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.Product.Create.Title()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<form.AppField
							name='status'
							children={field => (
								<field.SelectField
									label={L.Fields.Status()}
									options={[
										{ label: L.Products.Status('DRAFT'), value: 'DRAFT' },
										{
											label: L.Products.Status('PUBLISHED'),
											value: 'PUBLISHED'
										}
									]}
								/>
							)}
						/>
						<form.AppField
							name='title'
							children={field => <field.TextField autoFocus label={L.Product.Title()} />}
						/>
						<form.AppField
							name='subtitle'
							children={field => <field.TextField label={L.Product.Subtitle()} optional />}
						/>
						<form.AppField
							name='description'
							children={field => <field.TextAreaField label={L.Product.Description()} optional />}
						/>
						<div className='mt-4' />
						<form.AppField
							name='pricingMethod'
							children={field => (
								<field.SelectField
									label={L.Product.PricingMethodTitle()}
									options={[
										{
											label: L.Products.PricingMethod(PricingMethod.PerUnit),
											value: PricingMethod.PerUnit
										},
										{
											label: L.Products.PricingMethod(PricingMethod.PerWeight),
											value: PricingMethod.PerWeight
										},
										{
											label: L.Products.PricingMethod(PricingMethod.WeightEmbedded),
											value: PricingMethod.WeightEmbedded
										},
										{
											label: L.Products.PricingMethod(PricingMethod.PriceEmbedded),
											value: PricingMethod.PriceEmbedded
										}
									]}
								/>
							)}
						/>
						<form.AppField name='sku' children={field => <field.TextField label={L.Product.Sku()} />} />
					</FormFieldGroup>
					<div className='flex flex-col'>
						<div className='flex flex-row gap-2'>
							<div className='flex-1'>
								<FormLabel>{L.Product.Barcode.Barcode()}</FormLabel>
							</div>
							<div className='mx-2 my-1 flex h-4 w-4 items-end' />
						</div>
						<form.Field name='barcodes' mode='array'>
							{field => (
								<div className='flex flex-col items-stretch gap-y-4'>
									{field.state.value.map((_, i) => (
										<div key={i} className='flex flex-row items-center gap-x-2'>
											<div className='flex-1'>
												<form.AppField
													name={`barcodes[${i}]`}
													children={field => <field.TextField />}
												/>
											</div>
											{/* <div className='w-1/3'>
													<form.AppField
														name={`barcodes[${i}].type`}
														children={field => (
															<field.SelectField
																options={[
																	{
																		label: L.Products.BarcodeType('STANDARD'),
																		value: 'STANDARD'
																	},
																	{
																		label: L.Products.BarcodeType('PLU'),
																		value: 'PLU'
																	},
																	{
																		label: L.Products.BarcodeType(
																			'WEIGHT_EMBEDDED'
																		),
																		value: 'WEIGHT_EMBEDDED'
																	},
																	{
																		label: L.Products.BarcodeType(
																			'PRICE_EMBEDDED'
																		),
																		value: 'PRICE_EMBEDDED'
																	}
																]}
															/>
														)}
													/>
												</div> */}
											<div className='flex items-center self-stretch'>
												<Button
													type='button'
													variant='transparent'
													tabIndex={-1}
													size='small'
													onClick={() => field.removeValue(i)}
												>
													<Trash2 className='h-4 w-4 text-ui-fg-subtle' />
												</Button>
											</div>
										</div>
									))}
									<Button
										type='button'
										variant='secondary'
										size='small'
										className='self-start'
										onClick={() => field.pushValue('')}
									>
										<Plus className='mr-2 h-4 w-4' />
										{L.Product.Barcode.AddBarcode()}
									</Button>
								</div>
							)}
						</form.Field>
					</div>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>

					<form.SaveButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
